/**
 * NumberKey 相关工具函数
 * 从 MainPage.tsx 和其他文件提取，统一管理 NumberKey 相关逻辑
 */

import type { NumberKeyState, VBID } from '../types';

/**
 * 生成初始数字键状态
 * @param maxKeys - 最大数字键数量
 * @returns 初始化的数字键状态对象
 */
export const getInitialNumberKeyState = (maxKeys: number = 99): Record<string, NumberKeyState> => {
  const defaultState: Record<string, NumberKeyState> = {};
  
  for (let i = 1; i <= maxKeys; i++) {
    defaultState[i.toString()] = {
      keyNo: i,
      isActive: false,
      isABPoint: false,
      isPickUpState: false,
      timestamp: null,
      timecode: '00:00:00 00',
      videoMode: null,
      frame: null,
      note: '',
      screenshot: '',
      flag: 'off',
      playMode: 'M',
      // MV 模式专用字段
      mvUrl: '',
      mvTitle: '',
      mvDescription: '',
      mvThumbnail: ''
    };
  }
  
  return defaultState;
};

/**
 * 过滤重复的 VBID
 * @param vbids - VBID 数组
 * @returns 去重后的 VBID 数组
 */
export const filterDuplicateVBIDs = (vbids: VBID[]): VBID[] => {
  const uniqueVBIDs = new Map<string, VBID>();
  
  vbids.forEach(vbid => {
    // 使用 keyNo 而不是 timecode 来判断唯一性
    // 因为不同的检查点可能有相同的 timecode，但 keyNo 是唯一的
    const key = vbid.keyNo ? vbid.keyNo.toString() : `${vbid.timecode}_${vbid.mediaMode}`;
    
    if (!uniqueVBIDs.has(key)) {
      uniqueVBIDs.set(key, vbid);
    }
  });
  
  return Array.from(uniqueVBIDs.values());
};

/**
 * 将 VBID 数组转换为 NumberKeys 对象
 * @param vbids - VBID 数组
 * @returns NumberKeys 对象
 */
export const vbidsToNumberKeys = (vbids: VBID[]): Record<string, NumberKeyState> => {
  const numberKeys: Record<string, NumberKeyState> = {};
  
  vbids.forEach(vbid => {
    const keyNo = vbid.keyNo;
    if (keyNo) {
      numberKeys[keyNo.toString()] = {
        keyNo: keyNo,
        isActive: vbid.flag === 'on',
        isABPoint: false,
        isPickUpState: false,
        timestamp: vbid.timestamp || null,
        timecode: vbid.timecode || '00:00:00 00',
        videoMode: vbid.mediaMode as any || null,
        frame: vbid.frame || null,
        note: vbid.note || '',
        screenshot: vbid.screenshot || '',
        flag: vbid.flag || 'off',
        playMode: vbid.playMode || 'M',
        mvUrl: vbid.mvUrl || '',
        mvTitle: vbid.mvTitle || '',
        mvDescription: vbid.mvDescription || '',
        mvThumbnail: vbid.thumbnail || ''
      };
    }
  });
  
  return numberKeys;
};

/**
 * 查找第一个空白的数字键（未使用的最小编号）
 * @param numberKeys - 当前的 NumberKeys 对象
 * @param maxKeys - 最大数字键数量
 * @returns 第一个空白的数字键编号，如果没有则返回 null
 */
export const findFirstEmptyKey = (
  numberKeys: Record<string, NumberKeyState>,
  maxKeys: number = 99
): number | null => {
  // 找到所有已使用的键号
  const usedKeys = Object.entries(numberKeys)
    .filter(([key, state]) => state.isActive && parseInt(key) > 0)
    .map(([key]) => parseInt(key))
    .filter(key => !isNaN(key))
    .sort((a, b) => a - b);
  
  // 查找第一个未使用的键号
  for (let i = 1; i <= maxKeys; i++) {
    if (!usedKeys.includes(i)) {
      return i;
    }
  }
  
  return null; // 所有键号都已使用
};

/**
 * 获取活跃的数字键数量
 * @param numberKeys - NumberKeys 对象
 * @returns 活跃的数字键数量
 */
export const getActiveKeysCount = (numberKeys: Record<string, NumberKeyState>): number => {
  return Object.values(numberKeys).filter(state => state.isActive).length;
};

/**
 * 获取活跃的数字键编号列表（已排序）
 * @param numberKeys - NumberKeys 对象
 * @returns 活跃的数字键编号数组
 */
export const getActiveKeyNumbers = (numberKeys: Record<string, NumberKeyState>): number[] => {
  return Object.entries(numberKeys)
    .filter(([_, state]) => state.isActive)
    .map(([key]) => parseInt(key))
    .filter(key => !isNaN(key))
    .sort((a, b) => a - b);
};
